import React, { useCallback, useMemo, useState, useRef } from 'react';
import Text from '@amzn/meridian/text';
import moment from 'moment-timezone';
import Button from '@amzn/meridian/button';
import { Icon } from '@amzn/meridian';
import Column from '@amzn/meridian/column';
import chevronDownSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-down-small';
import chevronRightSmallTokens from '@amzn/meridian-tokens/base/icon/chevron-right-small';

import { chainWalk } from '../../../../../utils/helpers';
import {
  SSD_VOLUME_PROCESS_TYPE,
  BUSINESS_VIEW_NAME,
  GROCERY_ROLE_MAPPINGS,
  SSD_FEATURE_FLAGS_LIST,
  SSD_GROCERY_VOLUME_PROCESS_TYPE
} from '../../../../../utils/constants';
import { displayBusinessTabHeader, getTimelineForViewPlan, isFeatureFlagEnabled } from '../../../utils';
import { getSinglesEnabled } from '../SinglesUtils';
import {
  Pick2RebinOverridePopover,
  SinglesOverridePopover,
  GroceryOverridePopover
} from './OutboundVolumeOverridesByBusiness';
import { getCoreVolumesForOverrides, getCoreVolumesFromDemand } from './utils/coreUtils';
import { getGroceryVolumesForOverrides, getGroceryVolumesFromDemand } from './utils/groceryUtils';
import DynamicTable from '../../../../commonComponents/DynamicTable';
import BusinessTabsAndSpeeds from '../../../../commonComponents/BusinessTabsAndSpeeds';
import { getTableHeaders } from './utils/commonUtils';

const ViewOutboundVolumesByBusiness = ({ currentUser, isEditable, plan, setPlan }) => {
  const [selectedBusiness, setSelectedBusiness] = useState(BUSINESS_VIEW_NAME.CORE);
  const isGrocery = selectedBusiness === BUSINESS_VIEW_NAME.GROCERY;
  const timeline = getTimelineForViewPlan(plan);
  const adminSettings = JSON.parse(plan.input.adminSettings);

  const groceryCapacitySplit = chainWalk(() => adminSettings.grocery_charge_split, {
    pick_grocery_ambient: 0,
    pick_grocery_chilled: 0,
    pick_grocery_frozen: 0
  });
  const isDdpEnabled = chainWalk(() => adminSettings.ddp_enabled);
  const singlesEnabled = getSinglesEnabled(plan);
  const isRushEnabled = isFeatureFlagEnabled(plan.input.adminSettings, SSD_FEATURE_FLAGS_LIST.rushEnabled, false);
  const isGroceryEnabled = isFeatureFlagEnabled(plan.input.adminSettings, SSD_FEATURE_FLAGS_LIST.groceryEnabled, false);
  const outboundVolumes = chainWalk(() => plan.input.ssdShiftPlanInput.volume.outboundVolumeByBusiness, []);
  const timezone = chainWalk(() => adminSettings.timezone, moment.tz.guess());
  const demand = chainWalk(() => JSON.parse(plan.output.serializedOutput).transformedInputs.demand, []);
  const [collapsedRows, setCollapsedRows] = useState(new Set());
  const [groceryValuesMap, setGroceryValuesMap] = useState({});

  const updateGroceryValues = (cpt, newValues) => {
    setGroceryValuesMap((prev) => ({
      ...prev,
      [cpt]: newValues
    }));
  };

  const cptToLabel = useMemo(() => {
    const map = {};
    timeline.forEach(({ timestamp, label, date_time_local }) => {
      map[timestamp] = `${label} (${date_time_local.split('T')[1].slice(0, 5)} CPT)`;
    });
    return map;
  }, [timeline]);

  const cptToLocal = useMemo(() => {
    const map = {};
    timeline.forEach(({ timestamp, date_time_local }) => {
      map[timestamp] = date_time_local.split('T')[1].slice(0, 19);
    });
    return map;
  }, [timeline]);

  const updateCollapsedRows = (rowKey) => {
    const newSet = new Set(collapsedRows);
    newSet.has(rowKey) ? newSet.delete(rowKey) : newSet.add(rowKey);
    setCollapsedRows(newSet);
  };

  const renderTable = useCallback(
    (dataPerSpeed) => {
      const rows = [];
      Object.values(dataPerSpeed)
        .sort((a, b) => a.cpt - b.cpt)
        .forEach((volume) => {
          const { cpt } = volume;

          // CPT times coming from ATROPS end in 59, so we should account for those and adjust
          // https://w.amazon.com/bin/view/GSF/Teams/SCAR/Tech/TeamTacoma/Projects/Limelight/GroceryDesign/
          const isPlanningChargeCpt = isGrocery && cpt % 100 !== 0;
          const adjustedCpt = isPlanningChargeCpt ? cpt - (cpt % 100) : cpt;
          const rowKey = `row-${adjustedCpt}`;
          const cptMoment = moment.unix(adjustedCpt).tz(timezone);
          const cptDateTimeLocalLabel = cptToLabel[adjustedCpt];

          // Demand timestamps are formatting with ISO, so we adjust the CPT accordingly when retrieving from demand
          const cptISOString = cptMoment.format('YYYY-MM-DDTHH:mm:ss');

          const groceryValues = isEditable
            ? getGroceryVolumesForOverrides(volume, groceryCapacitySplit)
            : getGroceryVolumesFromDemand(demand, cptISOString);
          if (!cptDateTimeLocalLabel) return;

          if (isGrocery) {
            // Since ATROPS and Rodeo data come from different CPTs, we need to update any existing rows instead of
            // pushing new ones
            const existingRowIndex = rows.findIndex((row) => row.key === rowKey);
            const groceryPlanningCharge = {
              content: (
                <GroceryOverridePopover
                  currentUser={currentUser}
                  plan={plan}
                  setPlan={setPlan}
                  isEditable={isEditable}
                  outboundVolume={volume}
                  planningCharge={Math.round(groceryValues?.total.planningCharge)}
                  groceryCapacitySplit={groceryCapacitySplit}
                  groceryValues={groceryValues}
                  setGroceryValues={(updated) => updateGroceryValues(adjustedCpt, updated)}
                />
              )
            };
            const groceryRodeoBacklog = { content: groceryValues.total.rodeoBacklog };

            if (existingRowIndex >= 0) {
              if (isPlanningChargeCpt) {
                rows[existingRowIndex].cells[2] = groceryPlanningCharge;
              } else {
                rows[existingRowIndex].cells[3] = groceryRodeoBacklog;
              }
            } else {
              rows.push({
                key: rowKey,
                backgroundColor: 'secondary',
                highlightOnHover: true,
                cells: [
                  {
                    content: (
                      <Button type="icon" size="small" onClick={() => updateCollapsedRows(rowKey)}>
                        <Icon tokens={collapsedRows.has(rowKey) ? chevronRightSmallTokens : chevronDownSmallTokens} />
                      </Button>
                    ),
                    width: 48
                  },
                  {
                    content: (
                      <>
                        <Text type="h100">{cptDateTimeLocalLabel}</Text>
                        <Text type="h200" color="secondary">
                          {cptMoment.format('MM/DD/YYYY')}
                        </Text>
                      </>
                    ),
                    width: 300
                  },
                  groceryPlanningCharge,
                  groceryRodeoBacklog
                ]
              });
            }

            if (!collapsedRows.has(rowKey)) {
              Object.entries(SSD_GROCERY_VOLUME_PROCESS_TYPE).forEach(([_, entry]) => {
                const { label, value } = entry;
                // During shift planning, the volumes use admin settings and are organized by process role.
                // When viewing the OB Volume tab after a shift has been planned, the grocery roles are returned split by
                // volume type (ambient, chilled, frozen), so we need this mapping here
                const translatedValue = value in groceryValues ? value : GROCERY_ROLE_MAPPINGS[value].volumeType;
                const roleSpecificRowKey = `${rowKey}-${translatedValue}`;
                const planningCharge = { content: Math.round(groceryValues[translatedValue].planningCharge) };
                const rodeoBacklog = { content: Math.round(groceryValues[translatedValue].rodeoBacklog) };
                const existingRowIndex = rows.findIndex((row) => row.key === roleSpecificRowKey);
                if (existingRowIndex >= 0) {
                  if (isPlanningChargeCpt) {
                    rows[existingRowIndex].cells[2] = planningCharge;
                  } else {
                    rows[existingRowIndex].cells[3] = rodeoBacklog;
                  }
                } else {
                  rows.push({
                    key: roleSpecificRowKey,
                    cells: [{ content: null }, { content: label }, planningCharge, rodeoBacklog]
                  });
                }
              });
            }
          } else {
            const {
              pick2Rebin,
              singles,
              vna,
              sortable,
              total: totals
            } = isEditable
              ? getCoreVolumesForOverrides(volume, singlesEnabled)
              : getCoreVolumesFromDemand(demand, cptISOString, volume, singlesEnabled);

            rows.push({
              key: rowKey,
              backgroundColor: 'secondary',
              highlightOnHover: true,
              cells: [
                {
                  content: (
                    <Button type="icon" size="small" onClick={() => updateCollapsedRows(rowKey)}>
                      <Icon tokens={collapsedRows.has(rowKey) ? chevronRightSmallTokens : chevronDownSmallTokens} />
                    </Button>
                  ),
                  width: 48
                },
                {
                  content: (
                    <>
                      <Text type="h100">{cptDateTimeLocalLabel}</Text>
                      <Text type="h200" color="secondary">
                        {cptMoment.format('MM/DD/YYYY')}
                      </Text>
                    </>
                  ),
                  width: 300
                },
                { content: Math.round(sortable.sortablePredictedCharge), hidden: isDdpEnabled },
                { content: Math.round(sortable.sortableMaxPotentialCharge) },
                { content: Math.round(sortable.sortablePlanningCharge) },
                { content: Math.round(vna.vnaPredictedCharge), hidden: isDdpEnabled },
                { content: Math.round(totals.totalRodeoBacklog) },
                { content: Math.round(totals.totalPlanningVolume), hidden: isDdpEnabled }
              ]
            });

            if (!collapsedRows.has(rowKey)) {
              rows.push({
                key: `${rowKey}-pick2rebin`,
                cells: [
                  { content: null },
                  { content: SSD_VOLUME_PROCESS_TYPE.PICK_2_REBIN.label },
                  { content: Math.round(pick2Rebin.pickToRebinPredictedCharge), hidden: isDdpEnabled },
                  { content: Math.round(pick2Rebin.pickToRebinMaxPotentialCharge) },
                  {
                    content: (
                      <Pick2RebinOverridePopover
                        currentUser={currentUser}
                        plan={plan}
                        setPlan={setPlan}
                        isEditable={isEditable}
                        pickToRebinPlanningCharge={Math.round(pick2Rebin.pickToRebinPlanningCharge)}
                        outboundVolume={volume}
                        singlesEnabled={singlesEnabled}
                        volumeProcessType={SSD_VOLUME_PROCESS_TYPE.PICK_2_REBIN.value}
                      />
                    )
                  },
                  { content: '-', hidden: isDdpEnabled },
                  { content: Math.round(pick2Rebin.pickToRebinRodeoBacklog) },
                  {
                    content: Math.round(pick2Rebin.pickToRebinPlanningCharge + pick2Rebin.pickToRebinRodeoBacklog),
                    hidden: isDdpEnabled
                  }
                ]
              });

              if (singlesEnabled) {
                rows.push({
                  key: `${rowKey}-singles`,
                  cells: [
                    { content: null },
                    { content: SSD_VOLUME_PROCESS_TYPE.SINGLES.label },
                    { content: Math.round(singles.singlesPredictedCharge), hidden: isDdpEnabled },
                    { content: Math.round(singles.singlesPlanningCharge) },
                    {
                      content: (
                        <SinglesOverridePopover
                          currentUser={currentUser}
                          plan={plan}
                          setPlan={setPlan}
                          isEditable={isEditable}
                          singlesPlanningCharge={Math.round(singles.singlesPlanningCharge)}
                          outboundVolume={volume}
                          volumeProcessType={SSD_VOLUME_PROCESS_TYPE.SINGLES.value}
                        />
                      )
                    },
                    { content: '-', hidden: isDdpEnabled },
                    { content: Math.round(singles.singlesRodeoBacklog) },
                    {
                      content: Math.round(singles.singlesPlanningCharge + singles.singlesRodeoBacklog),
                      hidden: isDdpEnabled
                    }
                  ]
                });
              }

              rows.push({
                key: `${rowKey}-vna`,
                cells: [
                  { content: null },
                  { content: SSD_VOLUME_PROCESS_TYPE.VNA.label },
                  { content: '-', hidden: isDdpEnabled },
                  { content: '-' },
                  { content: '-' },
                  { content: Math.round(vna.vnaPredictedCharge), hidden: isDdpEnabled },
                  { content: Math.round(vna.vnaRodeoBacklog) },
                  {
                    content: Math.round(vna.vnaPredictedCharge + vna.vnaRodeoBacklog),
                    hidden: isDdpEnabled
                  }
                ]
              });
            }
          }
        });
      return (
        <DynamicTable
          headerRows={getTableHeaders({ isGrocery, isDdpEnabled })}
          rows={rows}
          showDividers={true}
          collapsedRows={collapsedRows}
          onToggleRowCollapse={updateCollapsedRows}
        />
      );
    },
    [outboundVolumes, groceryValuesMap, collapsedRows, cptToLabel, cptToLocal, isGrocery, plan, setPlan, singlesEnabled]
  );

  return (
    <Column width="100%">
      <div style={{ overflowX: 'auto', maxWidth: '100%' }}>
        <Text type={'h400'}>{displayBusinessTabHeader(selectedBusiness)} Outbound Volume</Text>
        <Text type={'b400'}>Verify or update the values below and submit.</Text>
        <BusinessTabsAndSpeeds
          isGroceryEnabled={isGroceryEnabled}
          isRushEnabled={isRushEnabled}
          data={outboundVolumes}
          renderChildComponent={renderTable}
          selectedBusiness={selectedBusiness}
          setSelectedBusiness={setSelectedBusiness}
          showSpeedAccordians={true}
        />
      </div>
    </Column>
  );
};

export default ViewOutboundVolumesByBusiness;
